import { normalizeEmail } from "@/lib/userSession";
import { isLocalhostRuntime } from "@/lib/adminAccess";
import { getLocalhostAdminClient } from "@/lib/adminClientSelection";

export const ADMIN_SELECTED_CLIENT_META_STORAGE_KEY = "optra_admin_selected_client_meta_v1";
export const ADMIN_SELECTED_CLIENT_STORAGE_KEY = "optra_admin_selected_client_v1";
export const ADMIN_CLIENT_SELECTED_EVENT = "optra-admin-client-selected";

export const isAdminLikeEmail = (value) => {
  const email = normalizeEmail(value || "");
  return email === "contact@optra-labs.com" || email === "admin@localhost.test";
};

export const clearAdminSelectedClientMeta = () => {
  if (typeof window === "undefined") return;
  if (isLocalhostRuntime()) {
    const localhostClient = getLocalhostAdminClient();
    try {
      window.localStorage.setItem(ADMIN_SELECTED_CLIENT_META_STORAGE_KEY, JSON.stringify(localhostClient));
      window.localStorage.setItem(ADMIN_SELECTED_CLIENT_STORAGE_KEY, localhostClient.key);
    } catch {
      // ignore
    }
    window.dispatchEvent(new CustomEvent(ADMIN_CLIENT_SELECTED_EVENT, { detail: { ...localhostClient, clientKey: localhostClient.key } }));
    return;
  }
  try {
    window.localStorage.removeItem(ADMIN_SELECTED_CLIENT_META_STORAGE_KEY);
    window.localStorage.removeItem(ADMIN_SELECTED_CLIENT_STORAGE_KEY);
  } catch {
    // ignore
  }
  window.dispatchEvent(new CustomEvent(ADMIN_CLIENT_SELECTED_EVENT, { detail: null }));
};

export const readAdminSelectedClientMeta = () => {
  if (typeof window === "undefined") return null;
  if (isLocalhostRuntime()) {
    const localhostClient = getLocalhostAdminClient();
    try {
      const parsed = JSON.parse(window.localStorage.getItem(ADMIN_SELECTED_CLIENT_META_STORAGE_KEY) || "null");
      const email = normalizeEmail(parsed?.email || "");
      const nextValue = email && !isAdminLikeEmail(email)
        ? {
            key: String(parsed?.key || (email ? `email:${email}` : "")),
            id: String(parsed?.id || ""),
            email,
            label: String(parsed?.label || email),
            profileSource: String(parsed?.profileSource || parsed?.profile_source || ""),
          }
        : email === localhostClient.email
        ? {
            key: String(parsed?.key || localhostClient.key),
            id: String(parsed?.id || ""),
            email: localhostClient.email,
            label: String(parsed?.label || localhostClient.label),
            profileSource: String(parsed?.profileSource || localhostClient.profileSource),
          }
        : localhostClient;
      window.localStorage.setItem(ADMIN_SELECTED_CLIENT_META_STORAGE_KEY, JSON.stringify(nextValue));
      window.localStorage.setItem(ADMIN_SELECTED_CLIENT_STORAGE_KEY, nextValue.key);
      return nextValue;
    } catch {
      try {
        window.localStorage.setItem(ADMIN_SELECTED_CLIENT_META_STORAGE_KEY, JSON.stringify(localhostClient));
        window.localStorage.setItem(ADMIN_SELECTED_CLIENT_STORAGE_KEY, localhostClient.key);
      } catch {
        // ignore
      }
      return localhostClient;
    }
  }
  try {
    const parsed = JSON.parse(window.localStorage.getItem(ADMIN_SELECTED_CLIENT_META_STORAGE_KEY) || "null");
    const email = normalizeEmail(parsed?.email || "");
    if (!email || isAdminLikeEmail(email)) {
      window.localStorage.removeItem(ADMIN_SELECTED_CLIENT_META_STORAGE_KEY);
      window.localStorage.removeItem(ADMIN_SELECTED_CLIENT_STORAGE_KEY);
      return null;
    }
    return {
      key: String(parsed?.key || ""),
      id: String(parsed?.id || ""),
      email,
      label: String(parsed?.label || email || ""),
      profileSource: String(parsed?.profileSource || ""),
    };
  } catch {
    return null;
  }
};

export const dispatchAdminSelectedClientChange = (payload = null) => {
  if (typeof window === "undefined") return;
  const nextPayload = payload && typeof payload === "object"
    ? {
        ...payload,
        clientKey: String(payload?.clientKey || payload?.key || ""),
      }
    : null;
  window.dispatchEvent(new CustomEvent(ADMIN_CLIENT_SELECTED_EVENT, { detail: nextPayload }));
};

export const writeAdminSelectedClientMeta = (payload = null) => {
  if (typeof window === "undefined") return;
  if (!payload || typeof payload !== "object") {
    clearAdminSelectedClientMeta();
    return;
  }
  const email = normalizeEmail(payload?.email || "");
  const key = String(payload?.key || (email ? `email:${email}` : "")).trim();
  const nextPayload = {
    key,
    id: String(payload?.id || ""),
    email,
    label: String(payload?.label || email || ""),
    profileSource: String(payload?.profileSource || ""),
  };
  try {
    if (key) {
      window.localStorage.setItem(ADMIN_SELECTED_CLIENT_STORAGE_KEY, key);
    } else {
      window.localStorage.removeItem(ADMIN_SELECTED_CLIENT_STORAGE_KEY);
    }
    window.localStorage.setItem(ADMIN_SELECTED_CLIENT_META_STORAGE_KEY, JSON.stringify(nextPayload));
  } catch {
    // ignore
  }
  dispatchAdminSelectedClientChange(nextPayload);
};

export const getResolvedAdminContextEmail = (sessionEmail = "") => {
  const selected = readAdminSelectedClientMeta();
  return selected?.email || normalizeEmail(sessionEmail || "") || "";
};

export const subscribeToAdminSelectedClient = (callback) => {
  if (typeof window === "undefined" || typeof callback !== "function") {
    return () => {};
  }

  const handler = () => callback(readAdminSelectedClientMeta());
  window.addEventListener("storage", handler);
  window.addEventListener(ADMIN_CLIENT_SELECTED_EVENT, handler);

  return () => {
    window.removeEventListener("storage", handler);
    window.removeEventListener(ADMIN_CLIENT_SELECTED_EVENT, handler);
  };
};
